// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { isAxiosError } from "axios"
import { apiClient } from "@/queries/client"
import type {
  SubmitCreditRequestInput,
  CreditRequestOutputModel,
  AdminCreditRequestOutputModel,
  ApproveCreditRequestInput,
} from "@/test/api-types"

export type {
  SubmitCreditRequestInput,
  CreditRequestOutputModel,
  AdminCreditRequestOutputModel,
  ApproveCreditRequestInput,
}

// The admin queue row as the Backend always populates it
// (`CreditRequestAdminEndpoints.ToOutputModel` sets every field but
// `github_username` unconditionally). `Required<>` narrows the
// generated schema's blanket optionality — an artifact of the Backend
// output model carrying no `[Required]`/NRT annotations, not a real
// "may be absent" — down to what this view can rely on.
export type AdminCreditRequestItem = Required<
  Omit<AdminCreditRequestOutputModel, "github_username">
> &
  Pick<AdminCreditRequestOutputModel, "github_username">

// Calls through the shared `apiClient` (ADR-0011); the wire is snake_case.
export const useSubmitCreditRequest = () => {
  return useMutation<
    CreditRequestOutputModel,
    unknown,
    SubmitCreditRequestInput
  >({
    mutationFn: (input) =>
      apiClient
        .post<CreditRequestOutputModel>("/front-api/credit-requests", input)
        .then((res) => res.data),
  })
}

// A 409 means the user already has a Pending request.
export const isDuplicatePendingRequest = (error: unknown): boolean =>
  isAxiosError(error) && error.response?.status === 409

export const getCreditRequestErrorMessage = (error: unknown): string => {
  if (isDuplicatePendingRequest(error)) {
    return "You already have a pending credit request — please wait for it to be resolved before submitting another."
  }
  return "Something went wrong — please try again."
}

// `enabled` is threaded through so the view can withhold the request
// entirely until the caller is confirmed admin — a non-admin gets no
// data, not just a hidden render.
export const ADMIN_CREDIT_REQUESTS_QUERY_KEY = ["adminCreditRequests"] as const

export const useAdminCreditRequests = (options?: { enabled?: boolean }) => {
  return useQuery<AdminCreditRequestItem[], Error>({
    queryKey: ADMIN_CREDIT_REQUESTS_QUERY_KEY,
    enabled: options?.enabled,
    queryFn: async () => {
      const response = await apiClient.get<AdminCreditRequestItem[]>(
        "/front-api/admin/credit-requests",
      )
      return response.data
    },
  })
}

export const useApproveCreditRequest = () => {
  const queryClient = useQueryClient()
  return useMutation<
    unknown,
    unknown,
    { id: number; grantedAmount: number }
  >({
    mutationFn: ({ id, grantedAmount }) =>
      apiClient
        .post(`/front-api/admin/credit-requests/${id}/approve`, {
          granted_amount: grantedAmount,
        } satisfies ApproveCreditRequestInput)
        .then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ADMIN_CREDIT_REQUESTS_QUERY_KEY,
      })
    },
  })
}

export const useRejectCreditRequest = () => {
  const queryClient = useQueryClient()
  return useMutation<unknown, unknown, number>({
    mutationFn: (id) =>
      apiClient
        .post(`/front-api/admin/credit-requests/${id}/reject`)
        .then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ADMIN_CREDIT_REQUESTS_QUERY_KEY,
      })
    },
  })
}
